import React, {ChangeEventHandler, CSSProperties, FC} from 'react';
import styled from 'styled-components';

import {EventHandler, HandlerOf, KeyboardEventHandler} from '../../../../helpers/src/react/reactHelpers';
import {appearanceNone, textSelection} from '../../styles/mixins';

/*
 * Props.
 */

export interface NumberInputInheritableProps {
  /** The initial value of the number input. */
  value?: number | '';
  /** Abstracted event handler providing the updated text. */
  onChange?: HandlerOf<number | ''>;
  /** Event handler called for every keydown event. */
  onKeyDown?: KeyboardEventHandler;
  /** Event handler called when the input is blurred. */
  onBlur?: EventHandler<HTMLInputElement>;
  /** The max value of the number input. */
  max?: number;
  /** The min value of the number input. */
  min?: number;
  /** Placeholder for the input. */
  placeholder?: string;
}

export interface NumberInputProps extends NumberInputInheritableProps {
  inputId?: string;
  className?: string;
  style?: CSSProperties;
  isDisabled?: boolean;
}

/*
 * Style.
 */

const StyledInput = styled.input`
  display: block;
  color: inherit;
  background: transparent;
  outline: 0;
  border: 0;
  padding: 0;

  ${appearanceNone()};
  ${textSelection()};

  ::placeholder {
    color: ${(p) => p.theme.greys.shade50};
  }
`;

/*
 * Component.
 */

export const NumberInput: FC<NumberInputProps> = (props) => {
  const onChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (props.onChange && event.currentTarget.value !== undefined) {
      const {value} = event.currentTarget;
      if (value === '') {
        props.onChange(value);
      } else {
        props.onChange(Number(value));
      }
    }
  };

  return (
    <StyledInput
      id={props.inputId}
      type="number"
      className={props.className}
      style={props.style}
      disabled={props.isDisabled}
      value={props.value}
      onChange={onChange}
      onKeyDown={props.onKeyDown}
      onBlur={props.onBlur}
      autoComplete="off"
      placeholder={props.placeholder}
      max={props.max}
      min={props.min}
    />
  );
};
